import { isDefined } from "@clipboard-health/util-ts";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import type { GeofenceDetectionMethod } from "@src/appV2/Location/geofence/useGeofence";
import { Button } from "@src/appV2/redesign/components/Button";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { BottomSheetIllustrationContent } from "@src/appV2/redesign/system/ui-components";
import { differenceInSeconds } from "date-fns";
import { useEffect, useRef } from "react";

import type { TimekeepingAction } from "./constants";

export interface TryAgainResult {
  resultInside: boolean;
  refetchDurationMs?: number;
}

export interface TooFarAwayContentProps {
  workplaceName: string;

  /**
   * Identifying context used to emit funnel analytics events. These fields
   * are forwarded to every `Too Far Away *` event so the events can be
   * correlated by shift/worker and joined to other timekeeping funnels.
   */
  action: Extract<TimekeepingAction, "clock-in" | "clock-out">;
  shiftId: string;
  workerId: string;
  workplaceId: string | undefined;
  detectionMethod: GeofenceDetectionMethod;
  currentInside: boolean;
  lastGeofenceChangeAt: Date | undefined;

  isRefetching: boolean;
  onTryAgain: () => Promise<TryAgainResult>;
  onSkipLocationCheck: () => void;
  onCancel: () => void;
}

export function TooFarAwayContent(props: TooFarAwayContentProps) {
  const {
    workplaceName,
    action,
    shiftId,
    workerId,
    workplaceId,
    detectionMethod,
    currentInside,
    lastGeofenceChangeAt,
    isRefetching,
    onTryAgain: onTryAgainProperty,
    onSkipLocationCheck: onSkipLocationCheckProperty,
    onCancel: onCancelProperty,
  } = props;

  const modalOpenedAtRef = useRef<Date>(new Date());
  const retryAttemptRef = useRef<number>(0);

  const baseEventPayload = { action, shiftId, workerId, workplaceId, detectionMethod };

  function secondsSinceModalOpen(): number {
    return differenceInSeconds(new Date(), modalOpenedAtRef.current);
  }

  function secondsSinceLastGeofenceChange(): number | undefined {
    if (!isDefined(lastGeofenceChangeAt)) {
      return undefined;
    }
    return differenceInSeconds(new Date(), lastGeofenceChangeAt);
  }

  useEffect(
    () => {
      logEvent(APP_V2_USER_EVENTS.TOO_FAR_AWAY_MODAL_OPENED, {
        ...baseEventPayload,
        currentInside,
        secondsSinceLastGeofenceChange: secondsSinceLastGeofenceChange(),
      });
    },
    // Fires once per mount — opening the modal is a single funnel entry.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  async function handleTryAgain() {
    retryAttemptRef.current += 1;
    const retryAttempt = retryAttemptRef.current;

    logEvent(APP_V2_USER_EVENTS.TOO_FAR_AWAY_TRY_AGAIN_TAPPED, {
      ...baseEventPayload,
      retryAttempt,
      currentInside,
      secondsSinceModalOpen: secondsSinceModalOpen(),
      secondsSinceLastGeofenceChange: secondsSinceLastGeofenceChange(),
    });

    const { resultInside, refetchDurationMs } = await onTryAgainProperty();

    logEvent(APP_V2_USER_EVENTS.TOO_FAR_AWAY_TRY_AGAIN_RESULT, {
      ...baseEventPayload,
      retryAttempt,
      secondsSinceModalOpen: secondsSinceModalOpen(),
      resultInside,
      ...(isDefined(refetchDurationMs) && { refetchDurationMs }),
    });
  }

  function handleSkipLocationCheck() {
    logEvent(APP_V2_USER_EVENTS.TOO_FAR_AWAY_SKIP_LOCATION_TAPPED, {
      ...baseEventPayload,
      retryAttempt: retryAttemptRef.current,
      secondsSinceModalOpen: secondsSinceModalOpen(),
    });
    onSkipLocationCheckProperty();
  }

  function handleCancel() {
    logEvent(APP_V2_USER_EVENTS.TOO_FAR_AWAY_MODAL_DISMISSED, {
      ...baseEventPayload,
      retryAttempt: retryAttemptRef.current,
      secondsSinceModalOpen: secondsSinceModalOpen(),
    });
    onCancelProperty();
  }

  return (
    <>
      <BottomSheetIllustrationContent
        illustrationType="location-error"
        title="Too far away"
        description={`It looks like you are not inside ${workplaceName}. Please try again when you are inside the facility. `}
      />
      <DialogFooter
        orientation="vertical"
        sx={{ marginTop: 4 }}
        closeButtonAriaLabel="Cancel"
        onClose={() => {
          handleCancel();
        }}
      >
        <LoadingButton
          fullWidth
          isLoading={isRefetching}
          variant="contained"
          size="large"
          onClick={async () => {
            await handleTryAgain();
          }}
        >
          I&apos;m inside the facility — Try again
        </LoadingButton>
        <Button
          fullWidth
          variant="outlined"
          size="large"
          onClick={() => {
            handleSkipLocationCheck();
          }}
        >
          Skip location confirmation
        </Button>
      </DialogFooter>
    </>
  );
}
